// @flow
import React from 'react';
import { connect } from 'react-redux';

import FilteredList from '../common/FilteredList';
import IconLabel from '../util/IconLabel';

import { ENTITY_TYPE_WORK, META_TITLE_PREFIX } from '../../constants';

import {
  selectFilteredAndSortedWorks,
  selectFilteredAndSortedWorksLoading,
  selectFilteredAndSortedWorksCount,
  selectNextFilteredWorksCursor,
  selectPreviousFilteredWorksCursor,
} from '../../selectors/work';
import { selectWorkFilterTopFacets, selectActiveWorkFacets } from '../../selectors/facet';
import { selectProfile } from '../../selectors/profile';

import type { Work } from '../../shapes/types';

import { selectWorksFilterParams, selectSortOrder } from '../../selectors/filteredList';
import * as facetActions from '../../actions/facet';
import * as localListSearchActions from '../../actions/localListSearch';
import * as workActions from '../../actions/work';
import * as clientActions from '../../actions/client';
import TitleWithPopularTitle from '../common/TitleWithPopularTitle';
import CapacitorRipple from '../capacitor/Ripple';
import { Link } from 'react-router';
import { FormattedMessage, injectIntl } from 'react-intl';
import dataComponent from '../../hoc/dataComponent';
import { compose } from 'redux';
import { messages } from '../messages/Profile';
import stylesFilteredList from '../common/FilteredList.css';
import stylesWorks from '../../views/Work.css';
import { selectSearchQuery } from '../../selectors/localListSearch';
import { getBaseHelmet } from '../../utils/profile';

declare var __CAPACITOR__: boolean;
declare var __ELECTRON__: boolean;

type OwnProps = {
  location: Object,
};

type MapStateToProps = {
  profileId: string,
  topFacets: Object,
  activeFacets: Array<Object>,
  works: Array<Work>,
  filterParams: Object,
  nextCursor?: string,
  prevCursor?: string,
  sort: string,
  searchTerm?: string,
  currentCursor?: string,
  worksAreLoading: boolean,
  worksCount: number,
};

type DispatchProps = {
  loadAllWorkFacets: Function,
  loadTopWorkFacets: Function,
  loadWorksByFilter: Function,
  setSearchQuery: Function,
};

type Props = OwnProps & MapStateToProps & DispatchProps;

const ProfileWorks = ({
  location,
  profileId,
  topFacets,
  activeFacets,
  works,
  filterParams,
  nextCursor,
  prevCursor,
  sort,
  searchTerm,
  currentCursor,
  worksAreLoading,
  worksCount,
  loadAllWorkFacets,
  loadTopWorkFacets,
  loadWorksByFilter,
  setSearchQuery,
}: Props) => {
  const onSearchChange = async term => {
    const params = selectWorksFilterParams(location.query, profileId, term);

    await loadWorksByFilter(params, currentCursor);
    await loadTopWorkFacets(params);
    setSearchQuery(term);
  };

  const renderWorkListItem = work => {
    return (
      <li className={stylesFilteredList.item} key={work.id}>
        <Link to={`/works/${work.id}`} className={stylesWorks.link}>
          <CapacitorRipple />
          <div>
            <TitleWithPopularTitle {...work} />
          </div>
          <IconLabel name="chevron-right" className={stylesWorks.linkIcon} size="default" />
        </Link>
      </li>
    );
  };

  return (
    <FilteredList
      contextType="profile"
      contextFacet="Composer"
      topFacets={topFacets}
      location={location}
      activeFacets={activeFacets}
      filterParams={filterParams}
      listIsEmpty={!works || works.length === 0}
      ignoredFacets={['composers']}
      nextCursor={nextCursor}
      prevCursor={prevCursor}
      loadMore={loadWorksByFilter}
      sort={sort}
      currentCursor={currentCursor}
      shouldRenderPlayAllButton={false}
      isLoading={worksAreLoading}
      emptyListMessage={
        <FormattedMessage
          id="filtered-recording-list.no-works"
          defaultMessage="No works match these criteria."
        />
      }
      items={works}
      itemsCount={worksCount}
      filterType="works"
      renderFilteredListItem={renderWorkListItem}
      loadAllFacets={loadAllWorkFacets}
      searchTerm={searchTerm}
      shouldShowSearchBox
      onSearchChange={onSearchChange}
    />
  );
};

ProfileWorks.onDataFetch = (store, params, location, intl) => {
  const state = store.getState();
  const profile = selectProfile(state, params.slug);
  const query = location.query;
  const filterParams = selectWorksFilterParams(query, profile.id);
  const { name } = profile;
  const title = intl.formatMessage(messages.profileWorksMetaTitle, { name });
  const description = intl.formatMessage(messages.profileWorksMetaDescription, { name });
  store.dispatch(
    clientActions.setHelmet({
      ...getBaseHelmet(profile, location, intl),
      title: META_TITLE_PREFIX + title,
      description,
      canonicalRoute: `/profiles/${params.slug}/works`,
      keywords: intl.formatMessage(messages.profileWorksMetaKeywords, { name }),
      ogTitle: title,
      ogDescription: description,
      currentCursor: params.cursor,
      nextCursor: selectNextFilteredWorksCursor(state, filterParams),
      prevCursor: selectPreviousFilteredWorksCursor(state, filterParams),
    })
  );
};

function mapStateToProps(state: Object, ownProps: OwnProps & { params: Object }): MapStateToProps {
  const { slug, cursor } = ownProps.params;
  const profile = selectProfile(state, slug);
  const query = ownProps.location.query;
  const searchTerm = selectSearchQuery(state, 'works');
  const filterParams = selectWorksFilterParams(query, profile.id, searchTerm);

  return {
    profileId: profile.id.toString(),
    topFacets: selectWorkFilterTopFacets(state, filterParams),
    activeFacets: selectActiveWorkFacets(state, filterParams, 'composers'),
    works: selectFilteredAndSortedWorks(state, filterParams),
    filterParams,
    nextCursor: selectNextFilteredWorksCursor(state, filterParams),
    prevCursor: selectPreviousFilteredWorksCursor(state, filterParams),
    sort: selectSortOrder(query, ENTITY_TYPE_WORK),
    searchTerm,
    currentCursor: cursor,
    worksAreLoading: selectFilteredAndSortedWorksLoading(state),
    worksCount: selectFilteredAndSortedWorksCount(state, filterParams),
  };
}

const searchKey = 'works';
function fetchData(store, { slug, cursor }, location) {
  const state = store.getState();
  const profile = selectProfile(state, slug);
  const { query } = location;
  const filterParams = selectWorksFilterParams(query, profile.id);
  return [
    store.dispatch(workActions.loadWorksByFilter(filterParams, cursor)),
    store.dispatch(facetActions.loadTopWorkFacets(filterParams)),
    store.dispatch(localListSearchActions.setSearchQuery(searchKey, query.searchTerm || '')),
  ];
}

const dispatchProps: DispatchProps = {
  loadWorksByFilter: workActions.loadWorksByFilter,
  loadAllWorkFacets: facetActions.loadAllWorkFacets,
  loadTopWorkFacets: facetActions.loadTopWorkFacets,
  setSearchQuery: searchTerm => localListSearchActions.setSearchQuery(searchKey, searchTerm),
};

export default compose(
  dataComponent(fetchData),
  connect(mapStateToProps, dispatchProps),
  injectIntl
)(ProfileWorks);
